// Fake job/plan/items per sample, shaped exactly like what a real
// GET /jobs/:id and GET /jobs/:id/plan response looks like — so
// SampleReview.jsx (which only knows the real shape) can render it with no
// special-casing. Reuses SAMPLE_DOCS for filename/sha/risk/entity-match
// rather than re-typing them — those must never drift between the sample
// picker (CaptureModal) and this breakdown.
//
// No rate_flags, no AI recommendation, on any sample: the reference
// prototype this was ported from never modeled either, and fabricating an
// AI recommendation would read as a real model output.
//
// `breakdown` (added 2026-08-05) is the render model for the full
// extraction-review layout the prototype shows for a picked sample: the
// rendered document page on the left, the flag rail in the middle, and the
// per-field extraction rail on the right. `plan.warnings` is DERIVED from
// those fields' flags below (warningsOf) rather than typed twice, so the
// flag chips, the pin cards, the per-field notes and the plan's own warning
// list can never disagree about how many issues a document has or how
// severe they are.
//
// `items` stays as the real per-item mutation-plan shape. Nothing renders it
// today (the breakdown's field rail replaced the generic item list), but it
// is what a real plan carries and what the row/queue code reads, so it is
// kept in step with the fields rather than deleted.
//
// See docs/superpowers/specs/2026-08-05-sample-review-breakdown-design.md
import { SAMPLE_DOCS, type SampleDoc } from './sampleDocs';

// ── Interfaces ──────────────────────────────────────────────────────────────

export interface Flag {
  id: string;
  kind: 'check' | 'blocker';
  sev: 'elevated' | 'high';
  title: string;
  body: string;
}

export interface Field {
  key: string;
  label: string;
  value: string;
  confidence: number;
  flag?: Flag;
}

export interface FlagWithFieldKey extends Flag {
  fieldKey: string;
}

export interface Warning {
  severity: string;
  message: string;
}

export interface DocSegment {
  text: string;
  f?: string;
}

export interface DocLine {
  t: 'title' | 'line' | 'bar' | 'total';
  text?: string;
  seg?: DocSegment[];
  w?: number;
  f?: string;
}

export interface BlastRadius {
  contracts?: string[];
  clins?: string[];
  projects?: string[];
  financial_impact?: { amount: number; type: string };
}

export interface Plan {
  risk_class: string;
  blast_radius: BlastRadius;
  blocking_errors: string[];
  warnings: Warning[];
}

export interface Breakdown {
  entity: string;
  blast: string;
  pageNote: string;
  doc: DocLine[];
  fields: Field[];
}

export interface Item {
  id: string;
  op: 'create' | 'update' | 'delete';
  target_table: string;
  confidence: number;
  proposed_value: Record<string, unknown>;
  before_value?: Record<string, unknown>;
}

export interface Job {
  id: string;
  original_filename: string;
  doc_type: string;
  sha256: string;
  status: string;
  target_entity_type?: string;
  entity_confidence?: number;
  risk_class?: string;
  model_id?: string;
  prompt_version?: string;
}

export interface SampleReviewEntry {
  job: Job;
  plan?: Plan;
  breakdown?: Breakdown;
  items?: Item[];
}

// ── Constants ───────────────────────────────────────────────────────────────

const DOC_TYPE: Record<string, string> = {
  invoice: 'invoice',
  roster: 'staffing_roster',
  mod: 'contract_mod',
  ims: 'schedule',
  cure: 'cure_notice',
  qasp: 'unclassified',
};

const TARGET_ENTITY_TYPE: Record<string, string> = {
  invoice: 'contract',
  roster: 'project',
  mod: 'contract',
  ims: 'project',
  cure: 'contract',
};

// The extractor identity the review header prints. Fabricated like the rest
// of this file, but stated once so the header line and any future per-field
// provenance read the same string.
const MODEL_ID = 'claude-sonnet-4-6';
const PROMPT_VERSION = 'v2.4';

function baseJob(key: string, status: string): Job {
  const d = SAMPLE_DOCS[key];
  const job: Job = {
    id: `sample-${key}`,
    original_filename: d.file,
    doc_type: DOC_TYPE[key],
    sha256: d.sha,
    status,
  };
  if (TARGET_ENTITY_TYPE[key]) job.target_entity_type = TARGET_ENTITY_TYPE[key];
  if (key !== 'qasp') {
    job.entity_confidence = d.entityConf / 100;
    job.risk_class = d.risk;
    job.model_id = MODEL_ID;
    job.prompt_version = PROMPT_VERSION;
  }
  return job;
}

// A field carries a `flag` when the checker had something to say about it.
//   kind: 'check' | 'blocker' — the badge, the pin/box severity, the card label.
//   sev:  'elevated' | 'high' — the severity a real plan.warnings entry carries.
// The two are separate on purpose: a field-level BLOCKER badge says "this
// field is why the document can't post as-is", which is not the same as the
// plan-level `blocking_errors` that stop Approve entirely (only `mod` has one).
const flagsOf = (fields: Field[]): FlagWithFieldKey[] =>
  fields.filter((f) => f.flag).map((f) => ({ ...f.flag!, fieldKey: f.key }));

const warningsOf = (fields: Field[]): Warning[] =>
  flagsOf(fields).map((f) => ({ severity: f.sev, message: `${f.title} — ${f.body}` }));

// ── invoice ────────────────────────────────────────────────────────────────
const INVOICE_FIELDS: Field[] = [
  { key: 'vendor', label: 'Vendor', value: 'ArcTide Systems, LLC', confidence: 0.99 },
  {
    key: 'invoice_number',
    label: 'Invoice number',
    value: 'INV-4417',
    confidence: 0.98,
    flag: {
      id: 'invoice-duplicate',
      kind: 'check',
      sev: 'elevated',
      title: 'Possible duplicate',
      body: 'INV-4409 posted 2026-07-28 carries the same period and the same $184,220.00 total. Confirm this is not a re-send.',
    },
  },
  { key: 'contract', label: 'Contract', value: '70CTD024C00000019', confidence: 0.94 },
  {
    key: 'clin',
    label: 'CLIN',
    value: '0002 — Engineering Services',
    confidence: 0.87,
    flag: {
      id: 'invoice-clin-name',
      kind: 'check',
      sev: 'elevated',
      title: 'CLIN name mismatch',
      body: 'Invoice reads "Engineering Services." The contract names CLIN 0002 "Engineering Services (Base)." Matched at 87% — confirm before posting.',
    },
  },
  { key: 'period_of_performance', label: 'Period of performance', value: '2026-07-01 → 2026-07-31', confidence: 0.96 },
  { key: 'labor_total', label: 'Labor total', value: '$171,400.00', confidence: 0.97 },
  { key: 'odc_total', label: 'ODC total', value: '$12,820.00', confidence: 0.95 },
  {
    key: 'total_amount_due',
    label: 'Total amount due',
    value: '$184,220.00',
    confidence: 0.97,
    flag: {
      id: 'invoice-funds',
      kind: 'blocker',
      sev: 'high',
      title: 'Exceeds funds available',
      body: 'CLIN 0002 has $143,020.00 remaining. Posting this invoice overruns the CLIN by $41,200.00 — needs a funding mod or a partial-pay decision.',
    },
  },
  {
    key: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 30',
    confidence: 0.93,
    flag: {
      id: 'invoice-terms',
      kind: 'check',
      sev: 'elevated',
      title: 'Terms differ from contract',
      body: 'Contract section G.4 sets Net 45. Paying on Net 30 pulls $184,220.00 into the September accrual.',
    },
  },
];

const INVOICE_DOC: DocLine[] = [
  { t: 'title', text: 'ARCTIDE SYSTEMS, LLC' },
  { t: 'line', seg: [{ text: 'Invoice INV-4417', f: 'invoice_number' }, { text: '   ·   Issued 2026-08-01' }] },
  { t: 'bar', w: 226 },
  { t: 'line', seg: [{ text: 'Contract: 70CTD024C00000019' }] },
  { t: 'line', seg: [{ text: 'CLIN 0002 — Engineering Services', f: 'clin' }] },
  { t: 'line', seg: [{ text: 'Period: 01 JUL 2026 – 31 JUL 2026' }] },
  { t: 'bar', w: 252 },
  { t: 'bar', w: 190 },
  { t: 'line', seg: [{ text: 'Direct labor .......... $171,400.00' }] },
  { t: 'line', seg: [{ text: 'Other direct costs .......... $12,820.00' }] },
  { t: 'total', f: 'total_amount_due', text: 'TOTAL DUE .......... $184,220.00' },
  { t: 'bar', w: 156 },
  { t: 'line', seg: [{ text: 'Terms: Net 30', f: 'payment_terms' }] },
];

// ── roster ─────────────────────────────────────────────────────────────────
const ROSTER_FIELDS: Field[] = [
  { key: 'project', label: 'Project', value: 'ICE OCIO Modernization', confidence: 0.98 },
  { key: 'effective_date', label: 'Effective date', value: '2026-08-11', confidence: 0.97 },
  { key: 'add_raghavan', label: 'New hire · CLIN 0002', value: 'Priya Raghavan — Sr. Cloud Engineer', confidence: 0.97 },
  { key: 'add_deel', label: 'New hire · CLIN 0002', value: 'Marcus Deel — DevSecOps Engineer', confidence: 0.97 },
  {
    key: 'add_iglesias',
    label: 'New hire · CLIN 0004',
    value: 'Tomás Iglesias — Data Engineer',
    confidence: 0.87,
    flag: {
      id: 'roster-clearance',
      kind: 'check',
      sev: 'elevated',
      title: 'Clearance not on file',
      body: 'CLIN 0004 requires an active Public Trust; no adjudication record is on file, so the 2026-08-18 start date may slip.',
    },
  },
  {
    key: 'rate_okafor',
    label: 'Rate change',
    value: 'Renée Okafor — Mid → Senior Engineer, effective 2026-08-01',
    confidence: 0.85,
    flag: {
      id: 'roster-retroactive',
      kind: 'check',
      sev: 'elevated',
      // 2026-08-01 against this file's own 2026-08-11 — ten days, and both
      // dates are stated on the screen, so the sentence has to agree with
      // them. It previously said "3 days", which matched no pair of dates
      // anywhere in the sample.
      title: 'Retroactive rate change',
      body: 'It takes effect 2026-08-01, ten days before this file\'s own 2026-08-11 effective date; a retroactive rate needs a contracting officer acknowledgement.',
    },
  },
  {
    key: 'drop_whitcomb',
    label: 'Removal',
    value: 'Dale Whitcomb — off-boarded 2026-07-29',
    confidence: 0.82,
    flag: {
      id: 'roster-key-personnel',
      kind: 'check',
      sev: 'high',
      title: 'Key personnel removal',
      body: 'Dale Whitcomb is named key personnel in section H.2; removal without a substitution letter is a contract deviation.',
    },
  },
  { key: 'burn_delta', label: 'Monthly burn delta', value: '+$38,400.00', confidence: 0.94 },
];

const ROSTER_DOC: DocLine[] = [
  { t: 'title', text: 'TEAM ROSTER UPDATE — AUGUST 2026' },
  { t: 'line', seg: [{ text: 'Project: ICE OCIO Modernization' }] },
  // "5 personnel actions", not "5 changes": the document enumerates five
  // roster moves, but the breakdown offers eight extracted fields to approve
  // and the overlay promises eight. Two different things called "changes" on
  // one screen read as the demo contradicting itself.
  { t: 'line', seg: [{ text: 'Effective 2026-08-11   ·   5 personnel actions' }] },
  { t: 'bar', w: 240 },
  { t: 'line', seg: [{ text: 'ADD    Priya Raghavan ..... Sr. Cloud Engineer' }] },
  { t: 'line', seg: [{ text: 'ADD    Marcus Deel ........ DevSecOps Engineer' }] },
  { t: 'line', seg: [{ text: 'ADD    Tomás Iglesias ..... Data Engineer · CLIN 0004', f: 'add_iglesias' }] },
  { t: 'bar', w: 208 },
  { t: 'line', seg: [{ text: 'RATE   Renée Okafor ....... Senior Engineer', f: 'rate_okafor' }] },
  { t: 'bar', w: 176 },
  { t: 'line', seg: [{ text: 'DROP   Dale Whitcomb ...... off-boarded 2026-07-29', f: 'drop_whitcomb' }] },
  { t: 'bar', w: 150 },
  { t: 'total', f: 'burn_delta', text: 'MONTHLY BURN ....... +$38,400.00' },
];

// ── mod ────────────────────────────────────────────────────────────────────
const MOD_FIELDS: Field[] = [
  {
    key: 'modification_number',
    label: 'Modification number',
    value: 'P00007',
    confidence: 0.96,
    flag: {
      id: 'mod-sequence-gap',
      kind: 'check',
      sev: 'high',
      title: 'P00006 is missing',
      body: 'Nothing between P00005 and P00007 has been ingested; the $250,000 gap in obligated value is almost certainly P00006.',
    },
  },
  { key: 'contract', label: 'Contract', value: '70CTD024C00000019', confidence: 0.99 },
  { key: 'effective_date', label: 'Effective date', value: '2026-08-15', confidence: 0.98 },
  { key: 'authority', label: 'Authority', value: 'FAR 43.103(a) — bilateral', confidence: 0.95 },
  { key: 'pop_extension', label: 'Period of performance', value: '2026-09-30 → 2026-12-29', confidence: 0.96 },
  {
    key: 'obligated_value',
    label: 'Obligated value',
    value: '$8,410,000.00 → $9,650,000.00',
    confidence: 0.93,
    flag: {
      id: 'mod-balance',
      kind: 'blocker',
      sev: 'high',
      title: 'Starting balance disagrees',
      body: 'The mod reads from $8,410,000; the system of record shows $8,160,000, a $250,000 difference that must reconcile.',
    },
  },
  { key: 'clin_add', label: 'CLIN added', value: '0005 — Transition Support, $412,000.00', confidence: 0.94 },
  {
    key: 'key_personnel',
    label: 'Key personnel',
    value: 'Program Manager clause updated',
    confidence: 0.84,
    flag: {
      id: 'mod-key-personnel',
      kind: 'check',
      sev: 'elevated',
      title: 'Low extraction confidence (84%)',
      body: 'The mod text does not name a replacement PM; read block 14 before accepting.',
    },
  },
];

const MOD_DOC: DocLine[] = [
  { t: 'title', text: 'MODIFICATION OF CONTRACT · SF-30' },
  { t: 'line', seg: [{ text: 'Modification P00007', f: 'modification_number' }, { text: '   ·   Effective 2026-08-15' }] },
  { t: 'bar', w: 232 },
  { t: 'line', seg: [{ text: 'Contract: 70CTD024C00000019' }] },
  { t: 'line', seg: [{ text: 'Authority: FAR 43.103(a) — bilateral' }] },
  { t: 'line', seg: [{ text: 'Block 14 — description of modification' }] },
  { t: 'bar', w: 250 },
  { t: 'bar', w: 194 },
  { t: 'line', seg: [{ text: 'Period ......... 2026-09-30 → 2026-12-29' }] },
  { t: 'line', seg: [{ text: 'CLIN 0005 ...... Transition Support, $412,000.00' }] },
  { t: 'total', f: 'obligated_value', text: 'OBLIGATED ... $8,410,000.00 → $9,650,000.00' },
  { t: 'bar', w: 150 },
  { t: 'line', seg: [{ text: 'Key personnel: PM clause updated', f: 'key_personnel' }] },
];

// ── cure ───────────────────────────────────────────────────────────────────
const CURE_FIELDS: Field[] = [
  {
    key: 'notice_type',
    label: 'Notice type',
    value: 'Cure notice',
    confidence: 0.88,
    flag: {
      id: 'cure-showcause',
      kind: 'check',
      sev: 'elevated',
      title: 'Could be a show-cause',
      body: 'Cure and show-cause notices share most language; a show-cause carries a shorter clock. Verify against the letterhead.',
    },
  },
  { key: 'issue_date', label: 'Issue date', value: '2026-08-02', confidence: 0.96 },
  { key: 'contract', label: 'Contract', value: '70CTD024C00000019', confidence: 0.97 },
  {
    key: 'cited_deficiency',
    label: 'Cited deficiency',
    value: 'Failure to deliver CDRL A004 within contractual schedule',
    confidence: 0.91,
    flag: {
      id: 'cure-a004-delivered',
      kind: 'check',
      sev: 'high',
      title: 'CDRL A004 shows delivered',
      body: 'projectbuilder recorded A004 as delivered 2026-07-24; either the receipt was never acknowledged or the wrong artifact was sent.',
    },
  },
  {
    key: 'response_deadline',
    label: 'Response deadline',
    value: '2026-08-12 (10 days per FAR 49.402-3(d))',
    confidence: 0.89,
    flag: {
      id: 'cure-clock',
      kind: 'check',
      sev: 'high',
      title: 'Clock runs from receipt',
      body: 'Receipt is not stated in this document, and the clock runs from receipt rather than the issue date — confirm the delivery date before relying on 2026-08-12.',
    },
  },
  { key: 'issuing_authority', label: 'Issuing authority', value: 'Danielle Moss, Contracting Officer', confidence: 0.95 },
];

const CURE_DOC: DocLine[] = [
  { t: 'title', text: 'CONTRACTING OFFICE · CURE NOTICE' },
  { t: 'line', seg: [{ text: 'CURE NOTICE', f: 'notice_type' }, { text: '   ·   Issued 2026-08-02' }] },
  { t: 'bar', w: 226 },
  { t: 'line', seg: [{ text: 'Contract: 70CTD024C00000019' }] },
  { t: 'line', seg: [{ text: 'To: ArcTide Systems, LLC' }] },
  { t: 'bar', w: 250 },
  { t: 'line', seg: [{ text: 'Deficiency: failure to deliver CDRL A004', f: 'cited_deficiency' }] },
  { t: 'bar', w: 202 },
  { t: 'bar', w: 168 },
  { t: 'total', f: 'response_deadline', text: 'RESPONSE DUE .......... 2026-08-12' },
  { t: 'bar', w: 150 },
  { t: 'line', seg: [{ text: 'Danielle Moss, Contracting Officer' }] },
];

// ── Exported data ───────────────────────────────────────────────────────────

export const SAMPLE_REVIEW_DATA: Record<string, SampleReviewEntry> = {
  invoice: {
    job: baseJob('invoice', 'awaiting_review'),
    plan: {
      risk_class: 'critical',
      blast_radius: {
        contracts: ['70CTD024C00000019'],
        clins: ['0002'],
        financial_impact: { amount: 41200, type: 'EAC shift' },
      },
      blocking_errors: [],
      warnings: warningsOf(INVOICE_FIELDS),
    },
    breakdown: {
      entity: SAMPLE_DOCS.invoice.entity,
      blast: 'Affects burn rate on CLIN 0002 · shifts Sept EAC by +$41,200 · appends to ArcTide Systems vendor ledger',
      pageNote: 'Page 1 of 3 · select a field to trace its source',
      doc: INVOICE_DOC,
      fields: INVOICE_FIELDS,
    },
    items: [
      {
        id: 'sample-invoice-1',
        op: 'create',
        target_table: 'invoices',
        // Below the 0.9 low-confidence threshold (matches the prototype's
        // own CLIN-field confidence).
        confidence: 0.87,
        proposed_value: {
          vendor: 'ArcTide Systems, LLC',
          invoice_number: 'INV-4417',
          contract: '70CTD024C00000019',
          clin: '0002 — Engineering Services',
          period_of_performance: '2026-07-01 → 2026-07-31',
          labor_total: 171400,
          odc_total: 12820,
          total_amount_due: 184220,
          payment_terms: 'Net 30',
        },
      },
    ],
  },

  roster: {
    job: baseJob('roster', 'awaiting_review'),
    plan: {
      risk_class: 'elevated',
      blast_radius: {
        clins: ['0002', '0004'],
        financial_impact: { amount: 38400, type: 'monthly burn increase' },
      },
      blocking_errors: [],
      warnings: warningsOf(ROSTER_FIELDS),
    },
    breakdown: {
      entity: SAMPLE_DOCS.roster.entity,
      blast: 'Affects CLIN 0002 and CLIN 0004 · raises monthly burn by +$38,400 · updates the ICE OCIO Modernization roster',
      pageNote: 'Page 1 of 2 · select a field to trace its source',
      doc: ROSTER_DOC,
      fields: ROSTER_FIELDS,
    },
    items: [
      { id: 'sample-roster-1', op: 'create', target_table: 'personnel', confidence: 0.97, proposed_value: { name: 'Priya Raghavan', role: 'Sr. Cloud Engineer', clin: '0002', start_date: '2026-08-11' } },
      { id: 'sample-roster-2', op: 'create', target_table: 'personnel', confidence: 0.97, proposed_value: { name: 'Marcus Deel', role: 'DevSecOps Engineer', clin: '0002', start_date: '2026-08-11' } },
      { id: 'sample-roster-3', op: 'create', target_table: 'personnel', confidence: 0.87, proposed_value: { name: 'Tomás Iglesias', role: 'Data Engineer', clin: '0004', start_date: '2026-08-18' } },
      { id: 'sample-roster-4', op: 'update', target_table: 'personnel', confidence: 0.85, proposed_value: { name: 'Renée Okafor', role: 'Senior Engineer', effective_date: '2026-08-01' }, before_value: { role: 'Mid Engineer' } },
      { id: 'sample-roster-5', op: 'delete', target_table: 'personnel', confidence: 0.82, proposed_value: { name: 'Dale Whitcomb', off_boarded: '2026-07-29' }, before_value: { role: 'Sr. Systems Engineer' } },
    ],
  },

  mod: {
    job: baseJob('mod', 'awaiting_review'),
    plan: {
      risk_class: 'critical',
      blast_radius: {
        contracts: ['70CTD024C00000019'],
        clins: ['0005'],
        financial_impact: { amount: 1240000, type: 'obligated value increase' },
      },
      blocking_errors: [
        'Sequence gap — the system holds mods P00001 through P00005; this mod assumes $8,410,000 obligated but the system shows $8,160,000. Ingest P00006 before applying this modification.',
      ],
      warnings: warningsOf(MOD_FIELDS),
    },
    breakdown: {
      entity: SAMPLE_DOCS.mod.entity,
      // "+$1,240,000" is 9,650,000 − 8,410,000: correct only if you accept the
      // mod's own starting balance, which the blocker directly above disputes
      // (the system holds 8,160,000, which would make the increase 1,490,000).
      // Stated flatly it read as a settled figure while the same screen argued
      // it was not one, so it is attributed to the mod and marked unreconciled.
      blast: 'Affects contract 70CTD024C00000019 · adds CLIN 0005 · the mod claims +$1,240,000 obligated, unreconciled against the $8,160,000 on record',
      pageNote: 'Page 1 of 4 · select a field to trace its source',
      doc: MOD_DOC,
      fields: MOD_FIELDS,
    },
    items: [
      {
        id: 'sample-mod-1',
        op: 'create',
        target_table: 'contract_mods',
        // Below the 0.9 low-confidence threshold, ties directly to the
        // "Low extraction confidence (84%)" warning above.
        confidence: 0.84,
        proposed_value: {
          modification_number: 'P00007',
          effective_date: '2026-08-15',
          authority: 'FAR 43.103(a) — bilateral',
          pop_extension: '2026-09-30 → 2026-12-29',
          funding_increment: '+$1,240,000 → $9,650,000 obligated',
          clin_add: 'CLIN 0005 — Transition Support, $412,000',
          key_personnel: 'Program Manager clause updated',
        },
      },
    ],
  },

  cure: {
    job: baseJob('cure', 'awaiting_review'),
    plan: {
      risk_class: 'critical',
      blast_radius: { contracts: ['70CTD024C00000019'] },
      blocking_errors: [],
      warnings: warningsOf(CURE_FIELDS),
    },
    breakdown: {
      entity: SAMPLE_DOCS.cure.entity,
      blast: 'Affects contract 70CTD024C00000019 · opens a cure-notice risk · response due 2026-08-12',
      pageNote: 'Page 1 of 2 · select a field to trace its source',
      doc: CURE_DOC,
      fields: CURE_FIELDS,
    },
    items: [
      {
        id: 'sample-cure-1',
        op: 'create',
        target_table: 'risks',
        confidence: 0.88,
        proposed_value: {
          notice_type: 'Cure notice',
          issue_date: '2026-08-02',
          cited_deficiency: 'Failure to deliver CDRL A004 within contractual schedule',
          response_deadline: '2026-08-12 (10 days per FAR 49.402-3(d))',
          issuing_authority: 'Danielle Moss, Contracting Officer',
        },
      },
    ],
  },

  ims: {
    job: baseJob('ims', 'awaiting_review'),
    plan: {
      risk_class: 'elevated',
      blast_radius: { projects: ['ICE OCIO Modernization'], clins: ['0001', '0002', '0003', '0004'] },
      blocking_errors: [],
      warnings: [],
    },
    items: [],
  },

  qasp: {
    job: baseJob('qasp', 'indexed_only'),
  },
};
